// Работа с примечаниями (причинами отклонения) заявки АБП в разрезе "по АБП" (EAK-1557).
// Все обращения к бэкенду собраны в этом файле.
//
// Бэкенд (EAK-286, BudgetExecutionAlterationAbpRequestController): таблица budget_execution_alteration_abp_reason,
// GET reason-list?requestId=<id заявки АБП>, POST reason (без id - upsert по заявке и классификации
// gr/abp/prg/ppr/spf/expense_code/plan_type), DELETE reason?id=. Обычно по ключу одна запись, но уникального
// индекса нет (а ППР null и 0 на фронте - один ключ), поэтому список всё равно группируется (groupReasons),
// а при удалении обрабатываются все id группы (deleteReasons).

export type ReasonPlanType = 1 | 2; // 1 - обязательства, 2 - платежи

export interface IAlterationReason {
    id: number | null;
    budgetExecutionAlterationAbpRequestId: number | string | null;
    budgetExecutionAlterationGuRequestId?: number | string | null;
    region: string | null;
    year: number | string | null;
    gr: number | null;
    abp: number | null;
    prg: number | null;
    ppr: number | null;
    spf: number | null;
    expenseCode: string | null; // null - причина на уровне специфики (без разреза по проекту)
    planType: ReasonPlanType;
    reasonRu: string | null;
    reasonKz: string | null;
    updateDate?: string | null;
}

// Группа записей с одинаковым ключом (копии по заявкам ГУ, консолидированным в заявку АБП)
export interface IReasonGroup {
    key: string;
    reason: IAlterationReason; // запись с максимальным updateDate (последняя актуальная)
    ids: number[]; // id всех записей группы - нужны для полного удаления по всем заявкам ГУ
    // Только для справки (EAK-808): причины всех консолидированных заявок АБП по этому ключу в порядке
    // номеров заявок; reason в этом случае - сводная запись (тексты склеены через перевод строки)
    items?: IAlterationReason[];
}

// Цель редактирования - строка специфики (expenseCode === null) либо проекта/трансферта в её составе
export interface IReasonTarget {
    planType: ReasonPlanType;
    gr: number | null;
    prg: number | null;
    ppr: number | null;
    spf: number | null;
    expenseCode: string | null;
    group: IReasonGroup | null;
}

// Статусы заявки, при которых примечание можно добавлять/редактировать/удалять
export const REASON_EDIT_STATUSES = [1, 28, 69];

// Ограничение длины текста примечания (maxlength textarea)
export const REASON_MAX_LENGTH = 1000;

const BASE_URL = '/api/budget-execution-alteration-abp-request/reason';

// Ключ группировки причин: prg/spf - через Number, ППР null и 0 считаются одинаковыми
// (аналогично getSpfKey в newAlterationForm.vue), expenseCode нормализуется к строке ('' - для специфики)
export function reasonKey(prg: any, ppr: any, spf: any, expenseCode: any, planType: ReasonPlanType): string {
    const pprNorm = Number(ppr) || 0;
    const expenseCodeNorm = (expenseCode === null || expenseCode === undefined || expenseCode === '')
        ? ''
        : String(expenseCode).trim();
    return `${planType}-${Number(prg)}-${pprNorm}-${Number(spf)}-${expenseCodeNorm}`;
}

export async function loadReasons(requestId: number | string): Promise<IAlterationReason[]> {
    const response = await fetch(`${BASE_URL}-list?requestId=${requestId}`);
    if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
    }
    return await response.json();
}

// Тело ответа не разбираем: после сохранения список всё равно перечитывается (loadReasons),
// а бэкенд может вернуть и пустой ответ - тогда response.json() дал бы ложную ошибку
export async function saveReason(dto: Partial<IAlterationReason>): Promise<void> {
    const response = await fetch(BASE_URL, {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify(dto)
    });
    if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
    }
}

// Удаляет все записи группы по одному id (DELETE reason?id=)
export async function deleteReasons(ids: number[]): Promise<void> {
    for (const id of ids) {
        const response = await fetch(`${BASE_URL}?id=${id}`, { method: 'DELETE' });
        if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
        }
    }
}

export function groupReasons(list: IAlterationReason[]): Record<string, IReasonGroup> {
    const groups: Record<string, IReasonGroup> = {};
    for (const item of list) {
        const key = reasonKey(item.prg, item.ppr, item.spf, item.expenseCode, item.planType);
        const existing = groups[key];
        if (!existing) {
            groups[key] = { key: key, reason: item, ids: item.id !== null ? [item.id] : [] };
        } else {
            if (item.id !== null) { existing.ids.push(item.id); }
            // из нескольких записей с одним ключом оставляем последнюю по updateDate
            if (!existing.reason.updateDate || (item.updateDate && item.updateDate > existing.reason.updateDate)) {
                existing.reason = item;
            }
        }
    }
    return groups;
}

// Текст примечания на языке интерфейса; если на нужном языке пусто - fallback на другой язык
export function reasonText(reason: IAlterationReason | null | undefined, locale: string): string {
    if (!reason) { return ''; }
    const primary = locale === 'kk' ? reason.reasonKz : reason.reasonRu;
    const fallback = locale === 'kk' ? reason.reasonRu : reason.reasonKz;
    if (primary && primary.trim()) { return primary; }
    return (fallback && fallback.trim()) ? fallback : '';
}

// ------------------------------------------------------------------------------------------------
// Справки (расходы), разрез "по АБП" - просмотр примечаний консолидированных заявок АБП (EAK-808).
//
// Справка - заявка уровня uf; её заявки АБП ссылаются на неё через budget_execution_alteration_abp_request_id.
// Причины всех заявок АБП справки отдаёт GET /api/budget-execution-alteration-uf-request/reason-list?requestId=<id справки>
// списком тех же записей, что и reason-list заявки АБП (поле budgetExecutionAlterationAbpRequestId - заявка АБП).
// Склейка выполняется на фронте (mergeReferenceReasons): если бэкенд уже вернёт по ключу одну сводную запись,
// она будет показана как есть.

const REFERENCE_REASON_LIST_URL = '/api/budget-execution-alteration-uf-request/reason-list';

// Ключ причины в справке: в справку консолидируются заявки разных АБП, поэтому к ключу добавляется код АБП
export function referenceReasonKey(abp: any, prg: any, ppr: any, spf: any, expenseCode: any, planType: ReasonPlanType): string {
    return `${Number(abp)}-${reasonKey(prg, ppr, spf, expenseCode, planType)}`;
}

// Разбивка причин справки по заявкам АБП. Порядок заявок - по возрастанию id (по мере создания, т.е. для
// одного АБП - по возрастанию номера заявки); внутри заявки порядок записей сохраняется.
function splitReasonsByRequest(list: IAlterationReason[]): IAlterationReason[][] {
    const byRequest = new Map<string, IAlterationReason[]>();
    for (const item of list) {
        const requestKey = String(item.budgetExecutionAlterationAbpRequestId);
        const requestList = byRequest.get(requestKey);
        if (requestList) {
            requestList.push(item);
        } else {
            byRequest.set(requestKey, [item]);
        }
    }
    return Array.from(byRequest.entries())
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(entry => entry[1]);
}

// Примечания справки: причины всех консолидированных в неё заявок АБП, сгруппированные по referenceReasonKey
export async function loadReferenceReasons(referenceId: number | string): Promise<Record<string, IReasonGroup>> {
    const response = await fetch(`${REFERENCE_REASON_LIST_URL}?requestId=${referenceId}`);
    if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
    }
    const list: IAlterationReason[] = await response.json();
    return mergeReferenceReasons(splitReasonsByRequest(list));
}

function joinReasonLines(values: Array<string | null>): string | null {
    const lines = values
        .map(value => (value || '').trim())
        .filter(value => !!value);
    return lines.length ? lines.join('\n') : null;
}

// Если в разных заявках АБП по одной специфике/проекту указаны разные причины - в справке выводятся все,
// разделитель - перевод строки (EAK-808). Порядок - как у заявок в listsByRequest.
export function mergeReferenceReasons(listsByRequest: IAlterationReason[][]): Record<string, IReasonGroup> {
    const itemsByKey: Record<string, IAlterationReason[]> = {};
    for (const list of listsByRequest) {
        // внутри одной заявки по ключу берём одну запись - последнюю по updateDate, как groupReasons в заявке АБП
        // (groupReasons напрямую не подходит: его ключ без кода АБП)
        const latestByKey: Record<string, IAlterationReason> = {};
        for (const item of list) {
            const key = referenceReasonKey(item.abp, item.prg, item.ppr, item.spf, item.expenseCode, item.planType);
            const existing = latestByKey[key];
            if (!existing || !existing.updateDate || (item.updateDate && item.updateDate > existing.updateDate)) {
                latestByKey[key] = item;
            }
        }
        for (const key of Object.keys(latestByKey)) {
            const item = latestByKey[key];
            // reasonText пуст, только если примечание не заполнено ни на одном языке
            if (!reasonText(item, 'ru')) { continue; }
            if (!itemsByKey[key]) { itemsByKey[key] = []; }
            itemsByKey[key].push(item);
        }
    }

    const groups: Record<string, IReasonGroup> = {};
    for (const key of Object.keys(itemsByKey)) {
        const items = itemsByKey[key];
        groups[key] = {
            key: key,
            reason: {
                ...items[0],
                id: null,
                reasonRu: joinReasonLines(items.map(item => item.reasonRu)),
                reasonKz: joinReasonLines(items.map(item => item.reasonKz))
            },
            ids: [],
            items: items
        };
    }
    return groups;
}

// Текст ячейки "Примечание". Для сводной группы справки язык выбирается по каждой заявке отдельно -
// иначе причина заявки, заполненная только на другом языке, потерялась бы при склейке.
export function reasonGroupText(group: IReasonGroup | null | undefined, locale: string): string {
    if (!group) { return ''; }
    if (group.items && group.items.length) {
        return group.items
            .map(item => reasonText(item, locale).trim())
            .filter(text => !!text)
            .join('\n');
    }
    return reasonText(group.reason, locale);
}
